import {
  HHButton,
  HHFormControlLabel,
  HHLoadingButton,
  HHStack,
  HHSwitch,
  HHTextField,
  HHToggleButton,
  HHToggleButtonGroup,
  HHTypography,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import {
  Controller,
  FormProvider,
  Resolver,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useDynamicDrawerContext } from '../../../context/dynamic-drawer.context';
import { ClientType, PackageType, PartnershipType } from '../../../types';
import { IDiscount } from '../../../utils/discount-utils';
import { ClientSelect } from '../../form-fields/client-select';
import { PartnershipSelect } from '../../form-fields/partnership-select';
import { ScopeSelector } from '../../form-fields/scope-selector';
import {
  DEFAULT_VBD_FORM_VALUES,
  ParsedVbdFormValues,
  VbdFormValues,
  VbdLevel,
} from './form-constants';
import { VbdValidationSchema } from './validation-schema';

export const parseFormValues = (data: VbdFormValues): ParsedVbdFormValues => ({
  level: data.level,
  // ACCOUNTS-AND-PLAN-MIGRATION: partnershipId/clientId are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  partnershipId: data.partnershipId,
  clientId: data.clientId,
  scopes: data.scopes,
  startDate: data.startDate ?? '',
  endDate: data.endDate,
  suppressEmptyCharges: data.suppressEmptyCharges,
  vbdTiers: data.tiers.map((tier, i) => ({
    tier: i + 1,
    discount: tier.discount ?? 0,
    subscriptionTier: {
      lower: i === 0 ? 1 : (data.tiers[i - 1].upperBound ?? 0),
      upper: i === data.tiers.length - 1 ? null : tier.upperBound,
    },
    milestoneDiscount: {
      c1: (tier.c1 ?? 0) / 100,
      c2: (tier.c2 ?? 0) / 100,
      c3: (tier.c3 ?? 0) / 100,
    },
  })),
});

export interface VbdFormProps {
  onSubmit: (values: ParsedVbdFormValues) => Promise<void>;
  initialValues?: VbdFormValues;
  editDefinitionId?: number;
  vbds: IDiscount[];
  partnerships: PartnershipType[];
  partnershipsLoading: boolean;
  clients: ClientType[];
  clientsLoading: boolean;
  packages: PackageType[];
}

export const VbdForm = ({
  onSubmit,
  initialValues,
  editDefinitionId,
  vbds,
  partnerships,
  partnershipsLoading,
  clients,
  clientsLoading,
  packages,
}: VbdFormProps): JSX.Element => {
  const { onClose } = useDynamicDrawerContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<VbdFormValues>({
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(
      VbdValidationSchema,
    ) as unknown as Resolver<VbdFormValues>,
    defaultValues: initialValues ?? {
      ...DEFAULT_VBD_FORM_VALUES,
      startDate: dayjs().format('YYYY-MM-DD'),
    },
    context: { vbds, editDefinitionId },
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid, isDirty },
    reset,
  } = methods;

  const { fields, append, remove } = useFieldArray({ control, name: 'tiers' });

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const isEditMode = !!initialValues;
  const level = watch('level');
  const tiers = watch('tiers');
  const watchedPartnershipId = watch('partnershipId');
  const watchedClientId = watch('clientId');
  const watchedStartDate = watch('startDate');
  const watchedEndDate = watch('endDate');

  // Re-run overlap checks when entity or dates change
  useEffect(() => {
    trigger('startDate');
    trigger('endDate');
  }, [watchedPartnershipId, watchedClientId, trigger]);

  useEffect(() => {
    if (watchedStartDate) trigger('endDate');
  }, [watchedStartDate, trigger]);

  useEffect(() => {
    trigger('startDate');
  }, [watchedEndDate, trigger]);

  const handleLevelChange = (_: React.MouseEvent, newLevel: VbdLevel): void => {
    if (!newLevel) return;
    setValue('level', newLevel, { shouldValidate: true });
    if (newLevel === 'client') {
      setValue('partnershipId', null, { shouldValidate: true });
    } else {
      setValue('clientId', null, { shouldValidate: true });
    }
  };

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (data: VbdFormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onSubmit(parseFormValues(data));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <HHStack spacing={4} direction="column">
          {/* Level */}
          <HHStack spacing={1}>
            <HHTypography hhVariant="body">Level</HHTypography>
            <HHToggleButtonGroup
              exclusive
              value={level}
              onChange={handleLevelChange}
              size="small"
              disabled={isEditMode}
              aria-label="VBD level"
            >
              <HHToggleButton
                hhVariant="variant-bypass"
                value="partnership"
                sx={{ textTransform: 'none' }}
              >
                Partnership Level
              </HHToggleButton>
              <HHToggleButton
                hhVariant="variant-bypass"
                value="client"
                sx={{ textTransform: 'none' }}
              >
                Client Level
              </HHToggleButton>
            </HHToggleButtonGroup>
          </HHStack>

          {/* Partnership or Client — mutually exclusive */}
          {level === 'client' ? (
            <ClientSelect
              options={clients}
              loading={clientsLoading}
              disabled={isEditMode}
              error={errors.clientId}
            />
          ) : (
            <PartnershipSelect
              options={partnerships}
              loading={partnershipsLoading}
              disabled={isEditMode}
              error={errors.partnershipId}
            />
          )}

          {/* Scope */}
          <ScopeSelector availablePackages={packages} />

          {/* Dates */}
          <HHStack direction="row" spacing={2}>
            <Controller
              control={control}
              name="startDate"
              render={({ field: { onChange, value } }): JSX.Element => (
                <DatePicker
                  label="Start Date"
                  value={value ? dayjs(value) : null}
                  disabled={isEditMode}
                  minDate={isEditMode ? undefined : dayjs()}
                  onChange={(newValue): void => {
                    onChange(
                      newValue ? dayjs(newValue).format('YYYY-MM-DD') : null,
                    );
                  }}
                  renderInput={(params): JSX.Element => (
                    <HHTextField
                      {...params}
                      hhVariant="variant-bypass"
                      fullWidth
                      error={!!errors.startDate}
                      helperText={errors.startDate?.message}
                    />
                  )}
                />
              )}
            />
            <Controller
              control={control}
              name="endDate"
              render={({ field: { onChange, value } }): JSX.Element => (
                <DatePicker
                  label="End Date (optional)"
                  value={value ? dayjs(value) : null}
                  onChange={(newValue): void => {
                    onChange(
                      newValue ? dayjs(newValue).format('YYYY-MM-DD') : null,
                    );
                  }}
                  renderInput={(params): JSX.Element => (
                    <HHTextField
                      {...params}
                      hhVariant="variant-bypass"
                      fullWidth
                      error={!!errors.endDate}
                      helperText={errors.endDate?.message}
                    />
                  )}
                />
              )}
            />
          </HHStack>

          {/* Suppress Empty Charges */}
          <Controller
            control={control}
            name="suppressEmptyCharges"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHFormControlLabel
                label="Suppress Empty Charges"
                control={
                  <HHSwitch
                    hhVariant="primary"
                    checked={value}
                    onChange={(e): void => onChange(e.target.checked)}
                  />
                }
              />
            )}
          />

          {/* Tiers */}
          <HHStack spacing={2}>
            <HHTypography hhVariant="section-title">Tiers</HHTypography>
            {fields.map((field, index) => {
              const isLastTier = index === fields.length - 1;
              const fromValue =
                index === 0 ? 1 : (tiers[index - 1]?.upperBound ?? 0);
              const tierErrors = errors.tiers?.[index];
              const tier = tiers[index];
              const cSumInvalid =
                tier?.c1 != null &&
                tier?.c2 != null &&
                tier?.c3 != null &&
                Math.round(tier.c1 + tier.c2 + tier.c3) !== 100;

              return (
                <HHStack
                  key={field.id}
                  spacing={2}
                  sx={{
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: 1,
                    padding: 2,
                  }}
                >
                  <HHStack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <HHTypography hhVariant="subtitle1">
                      Tier {index + 1}
                    </HHTypography>
                    {fields.length > 1 && isLastTier && (
                      <HHButton
                        hhVariant="variant-bypass"
                        variant="text"
                        size="small"
                        onClick={(): void => remove(index)}
                        type="button"
                      >
                        Remove
                      </HHButton>
                    )}
                  </HHStack>

                  {/* From / To */}
                  <HHStack direction="row" spacing={2} alignItems="flex-start">
                    <HHTextField
                      hhVariant="variant-bypass"
                      label="From (subscriptions)"
                      value={fromValue}
                      disabled
                      sx={{ flex: 1 }}
                    />
                    {isLastTier ? (
                      <HHTextField
                        hhVariant="variant-bypass"
                        label="To (subscriptions)"
                        value="∞"
                        disabled
                        sx={{ flex: 1 }}
                      />
                    ) : (
                      <Controller
                        control={control}
                        name={`tiers.${index}.upperBound`}
                        render={({
                          field: { onChange, value },
                        }): JSX.Element => (
                          <HHTextField
                            hhVariant="variant-bypass"
                            label="To (subscriptions)"
                            type="number"
                            value={value ?? ''}
                            onChange={(e): void => {
                              const parsed = parseInt(e.target.value, 10);
                              onChange(isNaN(parsed) ? null : parsed);
                            }}
                            error={!!tierErrors?.upperBound}
                            helperText={tierErrors?.upperBound?.message}
                            sx={{ flex: 1 }}
                            inputProps={{ min: 1 }}
                          />
                        )}
                      />
                    )}
                  </HHStack>

                  {/* Discount / C1 / C2 / C3 */}
                  <HHStack direction="row" spacing={2} alignItems="flex-start">
                    <Controller
                      control={control}
                      name={`tiers.${index}.discount`}
                      render={({ field: { onChange, value } }): JSX.Element => (
                        <HHTextField
                          hhVariant="variant-bypass"
                          label="Discount ($)"
                          type="number"
                          value={value ?? ''}
                          onChange={(e): void => {
                            const parsed = parseFloat(e.target.value);
                            onChange(isNaN(parsed) ? null : parsed);
                          }}
                          error={!!tierErrors?.discount}
                          helperText={tierErrors?.discount?.message}
                          sx={{ flex: 1 }}
                          inputProps={{ min: 0, step: '0.01' }}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`tiers.${index}.c1`}
                      render={({ field: { onChange, value } }): JSX.Element => (
                        <HHTextField
                          hhVariant="variant-bypass"
                          label="C1 %"
                          type="number"
                          value={value ?? ''}
                          onChange={(e): void => {
                            const parsed = parseFloat(e.target.value);
                            onChange(isNaN(parsed) ? null : parsed);
                          }}
                          error={!!tierErrors?.c1}
                          helperText={tierErrors?.c1?.message}
                          sx={{ flex: 1 }}
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`tiers.${index}.c2`}
                      render={({ field: { onChange, value } }): JSX.Element => (
                        <HHTextField
                          hhVariant="variant-bypass"
                          label="C2 %"
                          type="number"
                          value={value ?? ''}
                          onChange={(e): void => {
                            const parsed = parseFloat(e.target.value);
                            onChange(isNaN(parsed) ? null : parsed);
                          }}
                          error={!!tierErrors?.c2}
                          helperText={tierErrors?.c2?.message}
                          sx={{ flex: 1 }}
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name={`tiers.${index}.c3`}
                      render={({ field: { onChange, value } }): JSX.Element => (
                        <HHTextField
                          hhVariant="variant-bypass"
                          label="C3 %"
                          type="number"
                          value={value ?? ''}
                          onChange={(e): void => {
                            const parsed = parseFloat(e.target.value);
                            onChange(isNaN(parsed) ? null : parsed);
                          }}
                          error={!!tierErrors?.c3}
                          helperText={tierErrors?.c3?.message}
                          sx={{ flex: 1 }}
                          inputProps={{ min: 0, max: 100, step: '0.01' }}
                        />
                      )}
                    />
                  </HHStack>
                  {cSumInvalid && (
                    <HHTypography hhVariant="default" color="error">
                      C1 + C2 + C3 must equal 100%
                    </HHTypography>
                  )}
                </HHStack>
              );
            })}

            <HHButton
              hhVariant="variant-bypass"
              variant="outlined"
              size="small"
              onClick={(): void =>
                append({
                  upperBound: null,
                  discount: null,
                  c1: null,
                  c2: null,
                  c3: null,
                })
              }
              type="button"
            >
              + Add Tier
            </HHButton>
          </HHStack>

          {/* Actions */}
          <HHStack direction="row" spacing={2} justifyContent="flex-end">
            <HHButton
              hhVariant="variant-bypass"
              variant="text"
              onClick={handleCancel}
              type="button"
              disabled={isSubmitting}
            >
              Cancel
            </HHButton>
            <HHLoadingButton
              hhVariant="variant-bypass"
              variant="contained"
              type="submit"
              disabled={!isValid || !isDirty || isSubmitting}
              loading={isSubmitting}
            >
              {isEditMode ? 'Save' : 'Create'}
            </HHLoadingButton>
          </HHStack>
        </HHStack>
      </form>
    </FormProvider>
  );
};
